import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type Query, type QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { PAM_EVIDENCE_PAGE_SIZE } from '../pamEvidenceNodes';
import * as api from './client';
import { getPamQuestionOverview } from './questionClient';
import type {
  PamActionCostEstimateResponse,
  PamAutofillBookmarkletResponse,
  PamBoardReportStart,
  PamSaqComment,
  PamSaqCommentCreate,
  PamSaqCommentUpdate,
  PamSaqCompleteUpdate,
  PamSaqDeadlineUpdate,
  PamSaqNoteUpdate,
  PamEvidenceDocumentsResponse,
  PamSaqOverviewResponse,
  PamContactsResponse,
  PamContactsSaveRequest,
  PamSubmissionCreateRequest,
  PamSubmissionStatusResponse
} from './types';

export const pamQueryKeys = {
  submissions: () => ['pam', 'submissions'] as const,
  iterations: () => ['pam', 'iterations'] as const,
  dashboard: (submissionId: string) => ['pam', 'dashboard', submissionId] as const,
  questionsOverview: (submissionId: string, includeInaccessibleSaqs = false) =>
    ['pam', 'questions-overview', submissionId, includeInaccessibleSaqs ? 'all-saqs' : 'visible'] as const,
  /** Prefix matching both `visible` and `all-saqs` overview variants. */
  questionsOverviewPrefix: (submissionId: string) => ['pam', 'questions-overview', submissionId] as const,
  membersOverview: (submissionId: string) => ['pam', 'members-overview', submissionId] as const,
  fmMaturity: (submissionId: string) => ['pam', 'fm-maturity', submissionId] as const,
  tenantMembers: (activeOnly: boolean) => ['pam', 'tenant-members', activeOnly] as const,
  saqOverview: (saqId: string) => ['pam', 'saq', saqId] as const,
  discoverableGroups: () => ['pam', 'discoverable-groups'] as const,
  groupSaqs: (teamGroupId: string) => ['pam', 'group-saqs', teamGroupId] as const,
  question: (questionId: string) => ['pam', 'question', questionId] as const,
  costedActions: (submissionId: string) => ['pam', 'costed-actions', submissionId] as const,
  contacts: (submissionId: string) => ['pam', 'contacts', submissionId] as const,
  questionCostedActions: (questionId: string) => ['pam', 'question-costed-actions', questionId] as const,
  evidenceDocuments: (
    submissionId: string,
    siteId?: string | null,
    page?: number,
    questionId?: string | null,
    search?: string
  ) =>
    [
      'pam',
      'evidence-documents',
      submissionId,
      siteId ?? null,
      page ?? 1,
      questionId ?? null,
      search?.trim() || null
    ] as const,
  evidenceStructure: (submissionId: string, siteId?: string | null, search?: string) =>
    ['pam', 'evidence-structure', submissionId, siteId ?? null, search?.trim() || null] as const,
  evidenceSites: (submissionId: string) => ['pam', 'evidence-sites', submissionId] as const,
  questionActivity: (questionId: string) => ['pam', 'question-activity', questionId] as const,
  saqActivity: (saqId: string) => ['pam', 'saq-activity', saqId] as const
};

type PamEvidenceDocumentsQueryKey = ReturnType<typeof pamQueryKeys.evidenceDocuments>;

/** Keep previous page rows only when paginating the same evidence slice. */
function keepEvidenceDocumentsPagePlaceholder(
  submissionId: string,
  siteId: string | null | undefined,
  questionId: string | null,
  search: string | undefined
) {
  return (
    previousData: PamEvidenceDocumentsResponse | undefined,
    previousQuery:
      | Query<PamEvidenceDocumentsResponse, Error, PamEvidenceDocumentsResponse, PamEvidenceDocumentsQueryKey>
      | undefined
  ): PamEvidenceDocumentsResponse | undefined => {
    if (!previousData || !previousQuery) return undefined;
    const key = previousQuery.queryKey;
    if (key[2] !== submissionId) return undefined;
    if (key[3] !== (siteId ?? null)) return undefined;
    if (key[5] !== questionId) return undefined;
    if (key[6] !== (search?.trim() || null)) return undefined;
    return previousData;
  };
}

export function usePamSubmissions() {
  return useQuery({
    queryKey: pamQueryKeys.submissions(),
    queryFn: () => api.getPamSubmissions()
  });
}

export function usePamIterations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.iterations(),
    queryFn: () => api.getPamIterations(),
    enabled: options?.enabled ?? true
  });
}

export function useCreatePamSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PamSubmissionCreateRequest) => api.createPamSubmission(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.submissions() });
    }
  });
}

/**
 * Submitting or unlocking changes who may edit everything under the submission
 * (the Questions overview's per-SAQ edit standing, evidence, assignments), so
 * both drop every cached PAM read rather than patching individual entries.
 */
function invalidateAllPam(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ['pam'] });
}

export function useSubmitPamSubmission(submissionId: string) {
  const queryClient = useQueryClient();
  const mutationFn = useCallback(() => api.submitPamSubmission(submissionId), [submissionId]);
  return useMutation<PamSubmissionStatusResponse, Error, void>({
    mutationFn,
    onSuccess: () => invalidateAllPam(queryClient)
  });
}

export function useUnlockPamSubmission(submissionId: string) {
  const queryClient = useQueryClient();
  const mutationFn = useCallback(() => api.unlockPamSubmission(submissionId), [submissionId]);
  return useMutation<PamSubmissionStatusResponse, Error, void>({
    mutationFn,
    onSuccess: () => invalidateAllPam(queryClient)
  });
}

export function usePamDashboard(submissionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.dashboard(submissionId),
    queryFn: () => api.getPamDashboard(submissionId),
    enabled: (options?.enabled ?? true) && submissionId !== ''
  });
}

export function useGeneratePamAutofillBookmarklet(submissionId: string) {
  const mutationFn = useCallback(() => api.getPamAutofillBookmarklet(submissionId), [submissionId]);
  return useMutation<PamAutofillBookmarkletResponse, Error, void>({ mutationFn });
}

export function useStartPamBoardReport(submissionId: string) {
  const mutationFn = useCallback(() => api.startPamBoardReport(submissionId), [submissionId]);
  // The POST creates the chat and returns its id; the caller navigates there.
  // Failures surface through the global error notification (the client sets
  // globalError), so there is nothing to reconcile in the cache here.
  return useMutation<PamBoardReportStart, Error, void>({ mutationFn });
}

export function usePamQuestionsOverview(submissionId: string, options?: { includeInaccessibleSaqs?: boolean }) {
  const includeInaccessibleSaqs = options?.includeInaccessibleSaqs ?? false;
  return useQuery({
    queryKey: pamQueryKeys.questionsOverview(submissionId, includeInaccessibleSaqs),
    queryFn: () => api.getPamQuestionsOverview(submissionId, { includeInaccessibleSaqs }),
    enabled: submissionId !== ''
  });
}

// Admin-only (404s for non-leads); enabled only once the requester is known to
// be a PAM lead and the Assignments tab can actually be shown.
/**
 * FM Maturity Dimension Scores. Lead-only server-side (404s for anyone else),
 * so callers must gate the request on the requester being a PAM lead rather
 * than relying on an empty response.
 */
export function usePamFmMaturity(submissionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.fmMaturity(submissionId),
    queryFn: () => api.getPamFmMaturity(submissionId),
    enabled: (options?.enabled ?? true) && submissionId !== ''
  });
}

export function usePamMembersOverview(submissionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.membersOverview(submissionId),
    queryFn: () => api.getPamMembersOverview(submissionId),
    enabled: (options?.enabled ?? true) && submissionId !== ''
  });
}

/** Tenant roster used to enrich PAM member rows with email / job title. */
export function usePamTenantMembers(options?: { enabled?: boolean; activeOnly?: boolean }) {
  const activeOnly = options?.activeOnly ?? false;
  return useQuery({
    queryKey: pamQueryKeys.tenantMembers(activeOnly),
    queryFn: () => api.getPamTenantMembers(activeOnly),
    enabled: options?.enabled ?? true
  });
}

/**
 * Discoverable team groups in the requester's tenant — the SAQ-level group
 * picker source. Only enabled when the modal needs it (SAQ-level scope).
 */
export function usePamDiscoverableGroups(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.discoverableGroups(),
    queryFn: () => api.getPamDiscoverableGroups(),
    enabled: options?.enabled ?? true
  });
}

export function usePamSaqOverview(saqId: string) {
  return useQuery({
    queryKey: pamQueryKeys.saqOverview(saqId),
    queryFn: () => api.getPamSaqOverview(saqId),
    enabled: saqId !== ''
  });
}

export function useSaqActivity(saqId: string) {
  return useQuery({
    queryKey: pamQueryKeys.saqActivity(saqId),
    queryFn: () => api.getSaqActivity(saqId),
    enabled: saqId !== '',
    staleTime: 0,
    refetchOnMount: 'always'
  });
}

// The Costed Actions tab is admin-only (the endpoint 404s for non-leads), so
// only enable it once the requester is known to be a PAM lead.
export function usePamCostedActions(submissionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.costedActions(submissionId),
    queryFn: () => api.getPamCostedActions(submissionId),
    enabled: (options?.enabled ?? true) && submissionId !== ''
  });
}

export function usePamContacts(submissionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.contacts(submissionId),
    queryFn: () => api.getPamContacts(submissionId),
    enabled: (options?.enabled ?? true) && submissionId !== ''
  });
}

// The bulk save returns the refreshed tab, so the response seeds the cache
// directly rather than triggering a refetch.
export function useSavePamContacts(submissionId: string) {
  const queryClient = useQueryClient();
  return useMutation<PamContactsResponse, Error, PamContactsSaveRequest>({
    mutationFn: (payload: PamContactsSaveRequest) => api.savePamContacts(submissionId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(pamQueryKeys.contacts(submissionId), data);
    }
  });
}

// A POST that runs the LLM and returns a cost estimate without persisting
// anything, so it's a mutation (not a query) with no cache invalidation. The
// caller reads `error` to distinguish 404 (unavailable) from 503 (retryable).
export function useEstimateCostedActionCost() {
  return useMutation<PamActionCostEstimateResponse, Error, string>({
    mutationFn: (actionId: string) => api.estimateCostedActionCost(actionId)
  });
}

/** The applied form of an AI cost estimate the ActionModal keeps. */
export interface CostEstimateResult {
  revenue: number;
  capital: number;
  rationale: string;
}

/**
 * Turn a raw estimate response into the modal's revenue/capital result, treating
 * a null total as a failure: the model couldn't produce a usable estimate, so
 * we must not persist it as a real £0 cost.
 */
export function toCostEstimateResult(estimate: PamActionCostEstimateResponse): CostEstimateResult {
  if (estimate.cost === null) {
    throw new Error("The AI couldn't produce a usable cost estimate. Please try again.");
  }
  return {
    revenue: estimate.revenue_cost ?? 0,
    capital: estimate.capital_cost ?? 0,
    rationale: estimate.rationale
  };
}

/**
 * Human-readable message for an estimate failure, keeping the endpoint's
 * 404-vs-503 distinction: a 404 means the action can't be estimated at all
 * (not a PAM action / no access), whereas other failures are transient. Any
 * non-Axios error (e.g. the null-total guard above) surfaces its own message.
 */
export function describeCostEstimateError(err: unknown): string {
  if (err instanceof AxiosError) {
    // 404 (not a PAM costed action / no access) and 409 (the question isn't a
    // verdict type) are both permanent — the estimate can't run for this action,
    // so don't invite a pointless retry. Everything else is treated as transient.
    if (err.response?.status === 404 || err.response?.status === 409) {
      return "An AI cost estimate isn't available for this action.";
    }
    return 'The AI estimate service is unavailable right now. Please try again.';
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return "Couldn't generate an AI cost estimate. Please try again.";
}

/** Wrap an estimate failure in an Error carrying the user-facing message. */
export function toCostEstimateError(err: unknown): Error {
  return new Error(describeCostEstimateError(err));
}

/**
 * SAQs assigned to a team group — backs the group page's PAM tab. Member-only
 * (404s otherwise), so only enable it once the caller knows the current user is
 * a member of the group and the tenant has the PAM feature.
 */
export function usePamGroupSaqs(teamGroupId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: pamQueryKeys.groupSaqs(teamGroupId),
    queryFn: () => api.getPamGroupSaqs(teamGroupId),
    enabled: (options?.enabled ?? true) && teamGroupId !== ''
  });
}

function patchOverview(
  queryClient: QueryClient,
  saqId: string,
  patch: (old: PamSaqOverviewResponse) => PamSaqOverviewResponse
): void {
  const key = pamQueryKeys.saqOverview(saqId);
  queryClient.setQueryData<PamSaqOverviewResponse>(key, (old) => (old ? patch(old) : old));
}

function useSaqOverviewMutation<TPayload, TResult = void>(
  saqId: string,
  fn: (saqId: string, payload: TPayload) => Promise<TResult>,
  // Some mutations can update the cache surgically (comments); the rest just
  // invalidate the whole overview key.
  onResult?: (queryClient: QueryClient, result: TResult, payload: TPayload) => void
) {
  const queryClient = useQueryClient();
  const mutationFn = useCallback((payload: TPayload) => fn(saqId, payload), [fn, saqId]);
  const onSuccess = useCallback(
    (result: TResult, payload: TPayload) => {
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.saqActivity(saqId) });
      if (onResult !== undefined) {
        onResult(queryClient, result, payload);
        return;
      }
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.saqOverview(saqId) });
    },
    [onResult, queryClient, saqId]
  );
  return useMutation<TResult, Error, TPayload>({ mutationFn, onSuccess });
}

/**
 * One person's access to a submission is revoked one vector at a time from the
 * Assignments-tab member panel. Each removable vector is a REPLACE-minus-one:
 * re-read the authoritative current set, drop this user, write the rest back
 * via the existing replace endpoint. Group-inherited SAQ access and submission
 * ownership are NOT removable here — they're managed in the group / submission.
 */
export type PamMemberAccessRemoval =
  | { kind: 'submission'; userId: number }
  | { kind: 'domain'; submissionDomainId: string; userId: number }
  | { kind: 'saq'; submissionSaqId: string; userId: number }
  | { kind: 'question'; submissionSaqId: string; submissionQuestionId: string; userId: number };

export function useRemoveMemberAccess(submissionId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, PamMemberAccessRemoval>({
    mutationFn: async (target) => {
      if (target.kind === 'submission') {
        // Re-read current leads so we never accidentally clobber co-leads.
        const overview = await api.getPamMembersOverview(submissionId);
        const remaining = overview.members
          .filter((m) => m.access.is_submission_owner && m.user.user_id !== target.userId)
          .map((m) => m.user.email);
        await api.replaceSubmissionLeads(submissionId, { emails: remaining });
        return;
      }
      if (target.kind === 'domain') {
        // Domain owners live on the dashboard projection; re-read so we replace
        // the real current set (never clobber co-owners with a stale list).
        const dashboard = await api.getPamDashboard(submissionId);
        const domain = dashboard.domains.find((d) => d.submission_domain_id === target.submissionDomainId);
        if (domain === undefined) {
          // Guard: writing [] here would wipe every owner. Surface instead.
          throw new Error('Domain not found while removing ownership.');
        }
        const remaining = domain.owners.map((o) => o.user_id).filter((id) => id !== target.userId);
        await api.updateDomainOwners(submissionId, target.submissionDomainId, { user_ids: remaining });
        return;
      }
      if (target.kind === 'saq') {
        const overview = await api.getPamSaqOverview(target.submissionSaqId);
        const remaining = overview.user_assignees.map((a) => a.user.user_id).filter((id) => id !== target.userId);
        await api.updateSaqAssignees(submissionId, target.submissionSaqId, { user_ids: remaining });
        return;
      }
      // Normal (Question) and GuestQuestion assignees are separate role-sets on
      // the same question. Drive the removal off the question overview, which
      // keeps them apart, and REPLACE only the role(s) the target actually holds.
      // The SAQ overview's `assignee_user_ids` conflates both roles, so using it
      // here would promote guests into the normal set (and never remove guests).
      const question = await getPamQuestionOverview(target.submissionQuestionId);
      const normalIds = question.assignees.map((a) => a.user.user_id);
      const guestIds = question.guest_assignees.map((a) => a.user.user_id);
      if (normalIds.includes(target.userId)) {
        await api.updateQuestionAssignees(
          submissionId,
          target.submissionSaqId,
          target.submissionQuestionId,
          { user_ids: normalIds.filter((id) => id !== target.userId) },
          false
        );
      }
      if (guestIds.includes(target.userId)) {
        await api.updateQuestionAssignees(
          submissionId,
          target.submissionSaqId,
          target.submissionQuestionId,
          { user_ids: guestIds.filter((id) => id !== target.userId) },
          true
        );
      }
    },
    onSuccess: (_void, target) => {
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.membersOverview(submissionId) });
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.dashboard(submissionId) });
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.questionsOverviewPrefix(submissionId) });
      if (target.kind === 'saq' || target.kind === 'question') {
        queryClient.invalidateQueries({ queryKey: pamQueryKeys.saqOverview(target.submissionSaqId) });
      }
    }
  });
}

export function useUpdateSaqDeadline(saqId: string) {
  return useSaqOverviewMutation<PamSaqDeadlineUpdate>(saqId, api.updateSaqDeadline);
}

export function useUpdateSaqNote(saqId: string) {
  return useSaqOverviewMutation<PamSaqNoteUpdate>(saqId, api.updateSaqNote);
}

export function useUpdateSaqComplete(saqId: string) {
  return useSaqOverviewMutation<PamSaqCompleteUpdate>(saqId, api.updateSaqComplete, (queryClient, _result, payload) =>
    patchOverview(queryClient, saqId, (old) => ({ ...old, complete: payload.complete }))
  );
}

export function useMarkAllQuestionsAsNa(saqId: string) {
  const queryClient = useQueryClient();
  const mutationFn = useCallback(() => api.markAllQuestionsAsNa(saqId), [saqId]);
  return useMutation<void, Error, void>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.saqOverview(saqId) });
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.saqActivity(saqId) });
    }
  });
}

export function useAddSaqComment(saqId: string) {
  return useSaqOverviewMutation<PamSaqCommentCreate, PamSaqComment>(saqId, api.postSaqComment, (queryClient, created) =>
    patchOverview(queryClient, saqId, (old) => ({
      ...old,
      comments: [...old.comments, created]
    }))
  );
}

export function useEditSaqComment(saqId: string) {
  const queryClient = useQueryClient();
  const mutationFn = useCallback(
    ({ commentId, payload }: { commentId: string; payload: PamSaqCommentUpdate }) =>
      api.editSaqComment(saqId, commentId, payload),
    [saqId]
  );
  return useMutation<PamSaqComment, Error, { commentId: string; payload: PamSaqCommentUpdate }>({
    mutationFn,
    onSuccess: (updated) =>
      patchOverview(queryClient, saqId, (old) => ({
        ...old,
        comments: old.comments.map((c) => (c.id === updated.id ? updated : c))
      }))
  });
}

export function useRemoveSaqComment(saqId: string) {
  const queryClient = useQueryClient();
  const mutationFn = useCallback((commentId: string) => api.removeSaqComment(saqId, commentId), [saqId]);
  return useMutation<void, Error, string>({
    mutationFn,
    onSuccess: (_void, commentId) =>
      patchOverview(queryClient, saqId, (old) => ({
        ...old,
        comments: old.comments.filter((c) => c.id !== commentId)
      }))
  });
}

export function usePamEvidenceDocuments(
  submissionId: string,
  siteId?: string | null,
  opts?: {
    polling?: boolean;
    page?: number;
    pageSize?: number;
    questionId?: string | null;
    search?: string;
    enabled?: boolean;
  }
) {
  const page = opts?.page ?? 1;
  const pageSize = opts?.pageSize ?? PAM_EVIDENCE_PAGE_SIZE;
  const questionId = opts?.questionId ?? null;
  const search = opts?.search;
  return useQuery({
    queryKey: pamQueryKeys.evidenceDocuments(submissionId, siteId, page, questionId, search),
    queryFn: () => api.getPamEvidenceDocuments(submissionId, siteId, page, pageSize, questionId, search),
    placeholderData: keepEvidenceDocumentsPagePlaceholder(submissionId, siteId, questionId, search),
    enabled: opts?.enabled !== false,
    refetchInterval: (query) => {
      if (opts?.polling) return 5000;
      const pending = query.state.data?.pending_uploads ?? [];
      return pending.length > 0 ? 5000 : false;
    }
  });
}

export function usePamEvidenceStructure(submissionId: string, siteId?: string | null, search?: string) {
  return useQuery({
    queryKey: pamQueryKeys.evidenceStructure(submissionId, siteId, search),
    queryFn: () => api.getPamEvidenceStructure(submissionId, siteId, search)
  });
}

export function usePamEvidenceSites(submissionId: string) {
  return useQuery({
    queryKey: pamQueryKeys.evidenceSites(submissionId),
    queryFn: () => api.getPamEvidenceSites(submissionId)
  });
}

export function useLinkDocumentsAsEvidence(
  submissionId: string,
  saqId: string,
  questionId: string,
  siteIds: (string | null)[]
) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string[]>({
    mutationFn: (documentIds: string[]) =>
      api.linkDocumentsAsEvidence(questionId, {
        document_ids: documentIds,
        site_ids: siteIds
      }),
    onSuccess: () => {
      for (const siteId of siteIds) {
        queryClient.invalidateQueries({
          queryKey: ['pam', 'evidence-documents', submissionId, siteId]
        });
        queryClient.invalidateQueries({ queryKey: pamQueryKeys.evidenceStructure(submissionId, siteId) });
      }
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.saqOverview(saqId) });
      queryClient.invalidateQueries({ queryKey: pamQueryKeys.question(questionId) });
      queryClient.invalidateQueries({ queryKey: ['pam', 'dashboard'] });
    }
  });
}
